import { GridColumn, GridItem, GridRow } from "commons/ui/Grid";
import InputText from "commons/ui/input/InputText";
import LineSeparator from "commons/ui/LineSeparator";

import ExtensionsProporsionInfo from "components/subComponents/ExtensionsProporsionInfo";
import TwoLanguagesAdornment from "components/subComponents/TwoLanguagesAdornment";
import AttachmentPicker from "components/ui/AttachmentPicker";
import TitleFunctionsTopBlock from "components/ui/TitleFunctionsTopBlock";

import useCheckSectionErrors from "hooks/form/blocks/useCheckSectionErrors";
import useSectionTwoImagesFiveTexts from "hooks/form/blocks/useSectionTwoImagesFiveTexts";
import useTitleFunctionsTopBlock from "hooks/useTitleFunctionsTopBlock";

import { BlockEnum, DesktopOrMobile } from "types/enums";

function SectionTwoImagesFiveTextsForm({
    blockId,
    fourTexts = false,
}: {
    blockId: BlockEnum;
    fourTexts?: boolean;
}) {
    const { buttonDelete, setLeftImage, setRightImage, formHook } = useSectionTwoImagesFiveTexts({
        fourTexts: fourTexts,
    });

    useCheckSectionErrors({ formHook });

    const { checkErrorVisibility, setValue, form } = formHook;

    const titleFunctionsTopBlockHook = useTitleFunctionsTopBlock({
        buttonDelete: buttonDelete,
    });

    return (
        <>
            <TitleFunctionsTopBlock titleFunctionsTopBlockHook={titleFunctionsTopBlockHook} />

            {titleFunctionsTopBlockHook.closedBlock && (
                <>
                    <GridColumn>
                        <GridRow alignItems={"end"}>
                            <div className={"col-xxl-12-6 col-xl-12-12 col-xs-12-12"}>
                                <GridRow>
                                    <GridItem>
                                        <h3>
                                            {blockId === BlockEnum.SectionTextOnLeftBackground ||
                                            blockId === BlockEnum.SectionTextOnRightBackground
                                                ? "Immagine"
                                                : "Prima Immagine"}
                                        </h3>
                                    </GridItem>
                                </GridRow>

                                {(blockId === BlockEnum.SectionTextOnLeftBackground ||
                                    blockId === BlockEnum.SectionTextOnRightBackground) && (
                                    <ExtensionsProporsionInfo
                                        desktopText={"1200x 976px (max 400KB)"}
                                        mobileText={"1000x 814px (max 250KB)"}
                                        proportion={"16:9"}
                                    />
                                )}

                                <GridRow>
                                    <GridItem className={"col-xs-12-6"}>
                                        <AttachmentPicker
                                            buttonLabel={"Foto Desktop"}
                                            setFile={(file) =>
                                                setLeftImage(file, DesktopOrMobile.desktop)
                                            }
                                            file={form.left_img.desktop}
                                            accept={".png,.jpg,.jpeg"}
                                            errors={checkErrorVisibility("left_img.desktop")}
                                        />
                                    </GridItem>
                                    <GridItem className={"col-xs-12-6"}>
                                        <AttachmentPicker
                                            buttonLabel={"Foto Mobile"}
                                            setFile={(file) =>
                                                setLeftImage(file, DesktopOrMobile.mobile)
                                            }
                                            file={form.left_img.mobile}
                                            accept={".png,.jpg,.jpeg"}
                                            errors={checkErrorVisibility("left_img.mobile")}
                                        />
                                    </GridItem>
                                </GridRow>
                            </div>

                            <div className={"col-xxl-12-6 col-xl-12-12 col-xs-12-12"}>
                                <GridRow>
                                    <GridItem className={"col-xs-24-12"}>
                                        <h3>
                                            {blockId === BlockEnum.SectionTextOnLeftBackground ||
                                            blockId === BlockEnum.SectionTextOnRightBackground
                                                ? "Colore di sfondo"
                                                : "Seconda Immagine"}
                                        </h3>
                                    </GridItem>
                                </GridRow>

                                {(blockId === BlockEnum.SectionTextOnLeftBackground ||
                                    blockId === BlockEnum.SectionTextOnRightBackground) && (
                                    <GridItem className={"extensions-proporsion-info"}>
                                        <GridRow>
                                            <GridItem>
                                                <span>
                                                    Carica un’immagine piatta di un colore, in
                                                    formato{" "}
                                                    <span className={"strong-text"}>JPG</span>,
                                                    100x100px.
                                                </span>
                                            </GridItem>
                                        </GridRow>
                                    </GridItem>
                                )}

                                <GridRow>
                                    <GridItem className={"col-xs-12-6"}>
                                        <AttachmentPicker
                                            buttonLabel={"Foto Desktop"}
                                            setFile={(file) =>
                                                setRightImage(file, DesktopOrMobile.desktop)
                                            }
                                            file={form.right_img.desktop}
                                            accept={".png,.jpg,.jpeg"}
                                            errors={checkErrorVisibility("right_img.desktop")}
                                        />
                                    </GridItem>
                                    <GridItem className={"col-xs-12-6"}>
                                        <AttachmentPicker
                                            buttonLabel={"Foto Mobile"}
                                            setFile={(file) =>
                                                setRightImage(file, DesktopOrMobile.mobile)
                                            }
                                            file={form.right_img.mobile}
                                            accept={".png,.jpg,.jpeg"}
                                            errors={checkErrorVisibility("right_img.mobile")}
                                        />
                                    </GridItem>
                                </GridRow>
                            </div>
                        </GridRow>
                    </GridColumn>

                    <LineSeparator />

                    <GridColumn>
                        <GridRow>
                            <GridItem className={"col-xs-12-12"}>
                                <h3>Testi</h3>
                            </GridItem>
                        </GridRow>

                        <GridRow>
                            <GridItem>
                                <span>Inserisci i testi per tutte le lingue</span>
                            </GridItem>
                        </GridRow>
                    </GridColumn>

                    <GridColumn>
                        <GridRow>
                            <div className={"col-xs-12-6"}>
                                <GridRow>
                                    <GridItem className={"col-xs-12-12"}>
                                        <InputText
                                            label={"Titolo"}
                                            startAdornment={TwoLanguagesAdornment("it")}
                                            value={form.text.it.title}
                                            setValue={setValue}
                                            path={"text.it.title"}
                                            checkErrorVisibility={checkErrorVisibility}
                                            autoFocus
                                        />
                                    </GridItem>
                                </GridRow>

                                {!fourTexts && (
                                    <GridRow>
                                        <GridItem className={"col-xs-12-12"}>
                                            <InputText
                                                label={"Sottotitolo"}
                                                startAdornment={TwoLanguagesAdornment("it")}
                                                value={form.text.it.subtitle}
                                                setValue={setValue}
                                                path={"text.it.subtitle"}
                                                checkErrorVisibility={checkErrorVisibility}
                                            />
                                        </GridItem>
                                    </GridRow>
                                )}

                                <GridRow>
                                    <GridItem className={"col-xs-12-12"}>
                                        <InputText
                                            label={"Testo nel bottone"}
                                            startAdornment={TwoLanguagesAdornment("it")}
                                            value={form.text.it.button_label}
                                            setValue={setValue}
                                            path={"text.it.button_label"}
                                            checkErrorVisibility={checkErrorVisibility}
                                        />
                                    </GridItem>
                                </GridRow>

                                <GridRow>
                                    <GridItem className={"col-xs-12-12"}>
                                        <InputText
                                            label={"Url del bottone"}
                                            startAdornment={TwoLanguagesAdornment("it")}
                                            value={form.text.it.button_url}
                                            setValue={setValue}
                                            path={"text.it.button_url"}
                                            checkErrorVisibility={checkErrorVisibility}
                                        />
                                    </GridItem>
                                </GridRow>

                                <GridRow>
                                    <GridItem className={"col-xs-12-12"}>
                                        <InputText
                                            label={"Testo"}
                                            startAdornment={TwoLanguagesAdornment("it")}
                                            value={form.text.it.text}
                                            setValue={setValue}
                                            path={"text.it.text"}
                                            checkErrorVisibility={checkErrorVisibility}
                                            textarea
                                            largeBox
                                        />
                                    </GridItem>
                                </GridRow>
                            </div>

                            <div className={"col-xs-12-6"}>
                                <GridRow>
                                    <GridItem className={"col-xs-12-12"}>
                                        <InputText
                                            label={"Titolo"}
                                            startAdornment={TwoLanguagesAdornment("en")}
                                            value={form.text.en.title}
                                            setValue={setValue}
                                            path={"text.en.title"}
                                            checkErrorVisibility={checkErrorVisibility}
                                        />
                                    </GridItem>
                                </GridRow>

                                {!fourTexts && (
                                    <GridRow>
                                        <GridItem className={"col-xs-12-12"}>
                                            <InputText
                                                label={"Sottotitolo"}
                                                startAdornment={TwoLanguagesAdornment("en")}
                                                value={form.text.en.subtitle}
                                                setValue={setValue}
                                                path={"text.en.subtitle"}
                                                checkErrorVisibility={checkErrorVisibility}
                                            />
                                        </GridItem>
                                    </GridRow>
                                )}
                                <GridRow>
                                    <GridItem className={"col-xs-12-12"}>
                                        <InputText
                                            label={"Testo nel bottone"}
                                            startAdornment={TwoLanguagesAdornment("en")}
                                            value={form.text.en.button_label}
                                            setValue={setValue}
                                            path={"text.en.button_label"}
                                            checkErrorVisibility={checkErrorVisibility}
                                        />
                                    </GridItem>
                                </GridRow>

                                <GridRow>
                                    <GridItem className={"col-xs-12-12"}>
                                        <InputText
                                            label={"Url del bottone"}
                                            startAdornment={TwoLanguagesAdornment("en")}
                                            value={form.text.en.button_url}
                                            setValue={setValue}
                                            path={"text.en.button_url"}
                                            checkErrorVisibility={checkErrorVisibility}
                                        />
                                    </GridItem>
                                </GridRow>

                                <GridRow>
                                    <GridItem className={"col-xs-12-12"}>
                                        <InputText
                                            label={"Testo"}
                                            startAdornment={TwoLanguagesAdornment("en")}
                                            value={form.text.en.text}
                                            setValue={setValue}
                                            path={"text.en.text"}
                                            checkErrorVisibility={checkErrorVisibility}
                                            textarea
                                            largeBox
                                        />
                                    </GridItem>
                                </GridRow>
                            </div>
                        </GridRow>
                    </GridColumn>
                </>
            )}
        </>
    );
}

export default SectionTwoImagesFiveTextsForm;
